import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../hooks/useTranslation';

const Toaster = ({ toasts, removeToast }) => {
  const { isRTL } = useTranslation();
  
  return (
    <div className={`fixed top-4 sm:top-6 z-[70] space-y-2 sm:space-y-3 max-w-[calc(100%-2rem)] sm:max-w-md ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} removeToast={removeToast} />
      ))}
    </div>
  );
};

const Toast = ({ toast, removeToast }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { isRTL } = useTranslation();

  useEffect(() => {
    // Show toast with animation
    const showTimer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto dismiss after duration
    const dismissTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => removeToast(toast.id), 300); // Wait for fade out animation
    }, toast.duration || 5000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(dismissTimer);
    };
  }, [toast.id, toast.duration, removeToast]);

  const getToastStyles = (type) => {
    const baseStyles = "w-full sm:max-w-md bg-white shadow-xl rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden transform transition-all duration-300 border";
    
    switch (type) {
      case 'success':
        return `${baseStyles} ring-green-500 border-green-200`;
      case 'error':
        return `${baseStyles} ring-red-500 border-red-200`;
      case 'warning':
        return `${baseStyles} ring-yellow-500 border-yellow-200`;
      case 'info':
        return `${baseStyles} ring-blue-500 border-blue-200`;
      default:
        return `${baseStyles} ring-gray-500 border-gray-200`;
    }
  };

  const getIcon = (type) => {
    const iconClasses = "h-4 w-4 sm:h-5 sm:w-5";
    
    switch (type) {
      case 'success':
        return <CheckCircleIcon className={`${iconClasses} text-green-400`} />;
      case 'error':
        return <XCircleIcon className={`${iconClasses} text-red-400`} />;
      case 'warning':
        return <ExclamationTriangleIcon className={`${iconClasses} text-yellow-400`} />;
      case 'info':
        return <InformationCircleIcon className={`${iconClasses} text-blue-400`} />;
      default:
        return <InformationCircleIcon className={`${iconClasses} text-gray-400`} />;
    }
  };

  const getTitleColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-gray-800';
    }
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-green-700';
      case 'error':
        return 'text-red-700';
      case 'warning':
        return 'text-yellow-700';
      case 'info':
        return 'text-blue-700';
      default:
        return 'text-gray-700';
    }
  };

  return (
    <div
      className={`${getToastStyles(toast.type)} ${
        isVisible 
          ? 'translate-x-0 opacity-100' 
          : isRTL 
            ? '-translate-x-full opacity-0' 
            : 'translate-x-full opacity-0'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="p-3 sm:p-4">
        <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
          <div className="flex-shrink-0 mt-0.5">
            {getIcon(toast.type)}
          </div>
          <div className="flex-1 min-w-0">
            {toast.title && (
              <p className={`text-sm font-medium ${getTitleColor(toast.type)} break-words leading-tight ${isRTL ? 'text-right' : 'text-left'}`}>
                {toast.title}
              </p>
            )}
            {toast.message && (
              <p className={`${toast.title ? 'mt-1' : ''} text-sm ${getMessageColor(toast.type)} break-words leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                {toast.message}
              </p>
            )}
          </div>
          <div className={`flex-shrink-0 ${isRTL ? 'mr-2' : 'ml-2'}`}>
            <button
              type="button"
              className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2 p-1"
              onClick={() => {
                setIsVisible(false);
                setTimeout(() => removeToast(toast.id), 300);
              }}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom hook for managing toasts
export const useToaster = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast,
    };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccess = (title, message) => {
    addToast({ type: 'success', title, message });
  };

  const showError = (title, message) => {
    addToast({ type: 'error', title, message });
  };

  const showWarning = (title, message) => {
    addToast({ type: 'warning', title, message });
  };

  const showInfo = (title, message) => {
    addToast({ type: 'info', title, message });
  };

  return {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default Toaster; 